import { useRouter } from 'next/router'
import { FC, useEffect, useMemo } from 'react'

import { Size } from '@syconium/little-miss-figgy'

import { NotFoundPage } from '../../../../app/_not-found.client'
import { TrackPageView } from '../../../../app/_providers/TrackingProvider.client'
import { IterableContainer } from '../../../../containers/iterable'
import { usePagesDirectoryStableQueryParams } from '../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { singleQueryParamValue } from '../../../../lib/utils'
import { deriveSizes } from '../../../../lib/utils'

import { UseFetchProductPageDataOutput } from './hooks/useFetchProductPageData'
import { useIsGiftCard } from './hooks/useIsGiftCard'
import { usePdpServerSideProps } from './PdpServerSidePropsProvider'
import * as ProductSkeleton from './ProductPageSkeleton/ProductPageSkeleton'
import { ProductPageTemplate } from './ProductPageTemplate'

// Hoisted const objects are shared memory on the server. So they
// should only be immutable primitives. Regex creation is slow, so
// we don't do it in the render function which can repeat.
const DEFAULT_GIFT_DENOMINATION: string = '$100'

type ProductPageProps = UseFetchProductPageDataOutput

export const ProductPage: FC<ProductPageProps> = ({
  productGroup,
  productGroupStatus,
  featuredProduct,
  featuredProductStatus,
}) => {
  const { status: pdpServerSidePropsStatus, data: pdpServerSideProps } = usePdpServerSideProps()
  const { trackPdpView, isIterableReady } = IterableContainer.useContainer()
  const { asPath } = useRouter()
  const {
    query: { size: queryParamSizeUnsanitized },
  } = usePagesDirectoryStableQueryParams()
  const queryParamSize = singleQueryParamValue(queryParamSizeUnsanitized, undefined)

  useEffect(() => {
    const isReady =
      isIterableReady &&
      productGroupStatus === 'resolved' &&
      featuredProductStatus === 'resolved' &&
      productGroup &&
      featuredProduct

    // only fire page view when new product data is loaded
    if (isReady) {
      const selectedColor = featuredProduct.colorInfo?.rawName ?? ''
      const selectedFit = featuredProduct.rawFit ?? ''
      const selectedSize = queryParamSize ?? ''
      const path = asPath.split('?')[0] ?? asPath
      const productUrl = `${window.location.origin}${path}?color=${selectedColor}&fit=${selectedFit}&size=${selectedSize}`
      const variant = featuredProduct.variants?.find(variant => variant.size === selectedSize)
      trackPdpView({
        handle: featuredProduct.handle,
        defaultImage: featuredProduct.images?.nodes[0]?.source ?? '',
        path,
        productUrl,
        productName: productGroup.title,
        selectedColor,
        selectedFit,
        selectedSize,
        availability: featuredProduct.availableForSale,
        category: variant?.category,
        productId: featuredProduct.shopifyId,
        variantId: variant?.shopifyId ?? '',
        productGender: productGroup.genderCategory,
      })
    }
  }, [
    asPath,
    featuredProduct,
    featuredProductStatus,
    isIterableReady,
    productGroup,
    productGroupStatus,
    queryParamSize,
    trackPdpView,
  ])

  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup?.handle ?? '',
  })
  const selectedVariant = useMemo(() => {
    const sizes = deriveSizes(featuredProduct?.variants ?? [], productGroup?.sizes ?? [])

    let selectedSize: Size | undefined
    if (isGiftCard) {
      selectedSize =
        sizes.find(size => size.shorthand === queryParamSize) ??
        sizes.find(size => size.shorthand === DEFAULT_GIFT_DENOMINATION)
    } else {
      selectedSize =
        sizes.length === 1 ? sizes[0] : sizes.find(size => size.shorthand === queryParamSize)
    }

    if (featuredProduct?.variants?.length === 1) return featuredProduct.variants[0]
    return featuredProduct?.variants?.find(v => v.size === selectedSize?.shorthand)
  }, [featuredProduct, isGiftCard, productGroup, queryParamSize])

  // Show skeleton if we are idle prior to results, or pending on our first set of results
  if (
    productGroupStatus === 'idle' ||
    featuredProductStatus === 'idle' ||
    pdpServerSidePropsStatus === 'idle' ||
    ((!productGroup || !featuredProduct || !pdpServerSideProps) &&
      (productGroupStatus === 'pending' ||
        featuredProductStatus === 'pending' ||
        pdpServerSidePropsStatus === 'pending'))
  ) {
    return (
      <ProductSkeleton.Wrapper>
        <ProductSkeleton.PrimaryContent>
          <ProductSkeleton.Gallery withFitModelCaption />
          <ProductSkeleton.Details />
        </ProductSkeleton.PrimaryContent>
        <ProductSkeleton.WhyWeLove />
        <ProductSkeleton.FigsScrubsAre />
      </ProductSkeleton.Wrapper>
    )
  }

  // Show not found if no product was found or we had an error
  if (
    !productGroup ||
    !featuredProduct ||
    productGroupStatus === 'rejected' ||
    featuredProductStatus === 'rejected' ||
    pdpServerSidePropsStatus === 'rejected' ||
    pdpServerSidePropsStatus === 'not-found'
  ) {
    return <NotFoundPage />
  }

  return (
    <>
      <TrackPageView
        pageViewHandle={productGroup.handle}
        pageViewCategory='product'
        productShopifyId={featuredProduct.shopifyId}
      />
      <ProductPageTemplate
        productGroup={productGroup}
        featuredProduct={featuredProduct}
        productGroupStatus={productGroupStatus}
        featuredProductStatus={featuredProductStatus}
        selectedVariant={selectedVariant}
      />
    </>
  )
}
